import { useState, useEffect, useCallback, type ReactNode, type ReactElement } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { Dialog } from '@/components/common/Dialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { rpc, uuid } from '@/api/client';

export interface CrudColumn<T> {
  key: keyof T | string;
  label: string;
  className?: string;
  render?: (item: T) => ReactNode;
}

export interface CrudField {
  key: string;
  label: string;
  placeholder?: string;
  optional?: boolean;
  inputMode?: 'text' | 'decimal';
  type?: 'text' | 'select' | 'checkbox';
  options?: { value: string; label: string }[];
  /** Hint text shown below the field input. */
  info?: string;
  /** Compute a default value for new entities. Receives the current list of all items. */
  defaultValue?: (allItems: unknown[]) => string;
}

/** Render-prop for injecting extra form content below the standard fields. */
export type ExtraFormRenderer<T> = (ctx: {
  values: Record<string, string>;
  setValue: (key: string, val: string) => void;
  allItems: T[];
  editItem: T | null;
}) => ReactElement | null;

interface Props<T extends { id: string }> {
  title: string;
  entityName: string;
  emptyIcon: string;
  columns: CrudColumn<T>[];
  fields: CrudField[];
  listMethod: string;
  createMethod: string;
  updateMethod: string;
  deleteMethod: string;
  formGridClass?: string;
  /** Optional params to pass to the list RPC (e.g. { teamId }). */
  listParams?: Record<string, unknown>;
  /** If true, hide add/edit/delete controls (read-only view). */
  readOnly?: boolean;
  /** Extra values to inject into every create/update payload (e.g. { teamId }). */
  hiddenValues?: Record<string, string>;
  /** Optional callback to render extra form content below the standard fields. */
  renderExtraFormContent?: ExtraFormRenderer<T>;
  /** Called after a successful create or update with the entity ID and all form values. */
  onAfterSave?: (id: string, values: Record<string, string>) => Promise<void>;
  /** Custom validation before save. Return an error message string to block save, or null to allow. */
  validateBeforeSave?: (values: Record<string, string>) => string | null;
}

export function CrudPage<T extends { id: string }>({
  title,
  entityName,
  emptyIcon,
  columns,
  fields,
  listMethod,
  createMethod,
  updateMethod,
  deleteMethod,
  formGridClass,
  listParams,
  readOnly,
  hiddenValues,
  renderExtraFormContent,
  onAfterSave,
  validateBeforeSave,
}: Props<T>) {
  const { showToast } = useToast();
  const { t } = useI18n();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<T | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const listParamsKey = JSON.stringify(listParams ?? {});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await rpc<T[]>(listMethod, listParams);
      setItems(data);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [listMethod, listParamsKey]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSave = async (values: Record<string, string>) => {
    const requiredFields = fields.filter(f => !f.optional && f.type !== 'checkbox');
    const missing = requiredFields.find(f => !values[f.key]?.trim());
    if (missing) {
      showToast(t('crud.requiredField', { field: missing.label }), 'error');
      return;
    }

    if (validateBeforeSave) {
      const validationError = validateBeforeSave(values);
      if (validationError !== null) {
        if (validationError) showToast(validationError, 'error');
        return;
      }
    }

    // Convert checkbox string values to actual booleans for the API.
    const payload: Record<string, string | boolean> = { ...values, ...hiddenValues };
    for (const f of fields) {
      if (f.type === 'checkbox') {
        payload[f.key] = values[f.key] === 'true';
      }
    }

    setSaving(true);
    try {
      let savedId: string;
      if (editItem) {
        savedId = editItem.id;
        await rpc(updateMethod, { id: editItem.id, ...payload });
        showToast(t('crud.updated', { entity: entityName }));
      } else {
        savedId = uuid();
        await rpc(createMethod, { id: savedId, ...payload });
        showToast(t('crud.created', { entity: entityName }));
      }
      if (onAfterSave) {
        await onAfterSave(savedId, values);
      }
      setShowForm(false);
      setEditItem(null);
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: T) => {
    const name = `${(item as Record<string, unknown>).brand ?? ''} ${(item as Record<string, unknown>).type ?? ''}`.trim();
    if (!confirm(t('crud.confirmDelete', { entity: entityName.toLowerCase(), name }))) return;
    try {
      await rpc(deleteMethod, { id: item.id });
      showToast(t('crud.deleted', { entity: entityName }));
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const startEdit = (item: T) => {
    setEditItem(item);
    setShowForm(true);
  };

  const startAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditItem(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {!readOnly && (
          <button
            onClick={startAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
              bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <Plus size={14} />
            {t('common.add')}
          </button>
        )}
      </div>

      {/* Form Dialog */}
      {showForm && (
        <FormDialog
          title={editItem ? t('crud.editEntity', { entity: entityName }) : t('crud.newEntity', { entity: entityName })}
          fields={fields}
          initialValues={editItem as Record<string, string> | null}
          allItems={items}
          saving={saving}
          saveLabel={editItem ? t('common.save') : t('common.create')}
          onSave={handleSave}
          onClose={cancelForm}
          renderExtraFormContent={renderExtraFormContent ? (values, setValue) => renderExtraFormContent({ values, setValue, allItems: items, editItem }) : undefined}
        />
      )}

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={emptyIcon}
          message={t('crud.emptyState', { title: title.toLowerCase(), entity: entityName.toLowerCase() })}
        />
      ) : (
        <CrudTable
          items={items}
          columns={columns}
          onEdit={startEdit}
          onDelete={handleDelete}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

/* -- Form Dialog -------------------------------------------------------- */

interface FormDialogProps {
  title: string;
  fields: CrudField[];
  initialValues: Record<string, string> | null;
  allItems: unknown[];
  saving: boolean;
  saveLabel: string;
  onSave: (values: Record<string, string>) => void;
  onClose: () => void;
  renderExtraFormContent?: (values: Record<string, string>, setValue: (key: string, val: string) => void) => ReactElement | null;
}

function FormDialog({ title, fields, initialValues, allItems, saving, saveLabel, onSave, onClose, renderExtraFormContent }: FormDialogProps) {
  const { t } = useI18n();
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    // Initialize standard field keys.
    fields.forEach(f => {
      if (initialValues) {
        const raw = (initialValues as Record<string, unknown>)?.[f.key];
        v[f.key] = raw === true || raw === 'true' ? 'true' : raw === false || raw === 'false' ? 'false' : (raw as string) ?? '';
      } else {
        v[f.key] = f.defaultValue ? f.defaultValue(allItems) : '';
      }
    });
    // Also preserve any extra keys from initialValues (e.g. tire IDs from renderExtraFormContent).
    if (initialValues) {
      for (const [k, val] of Object.entries(initialValues)) {
        if (!(k in v) && typeof val === 'string') {
          v[k] = val;
        }
      }
    }
    return v;
  });
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const set = (key: string, val: string) =>
    setValues(prev => ({ ...prev, [key]: val }));

  const isFieldInvalid = (f: CrudField) =>
    hasAttemptedSave && f.type !== 'checkbox' && !f.optional && !values[f.key]?.trim();

  const handleSubmit = () => {
    setHasAttemptedSave(true);
    const trimmed: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) trimmed[k] = v.trim();
    onSave(trimmed);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
              hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
          >
            {saveLabel}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
              border border-border-default hover:border-border-hover hover:text-text-primary transition-colors cursor-pointer"
          >
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {fields.map(f => (
          <div key={f.key} className="flex flex-col gap-1.5">
            {f.type === 'checkbox' ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={values[f.key] === 'true'}
                  onChange={e => set(f.key, e.target.checked ? 'true' : 'false')}
                  className="w-4 h-4 rounded border-border-default text-accent focus:ring-accent"
                />
                <span className="text-xs font-medium text-text-primary">{f.label}</span>
              </label>
            ) : (
              <>
                <label className="text-xs font-medium text-text-muted">
                  {f.label}
                  {f.optional && <span className="text-text-muted ml-1 font-normal">{t('common.optional')}</span>}
                </label>
                {f.type === 'select' && f.options ? (
                  <select
                    value={values[f.key] ?? ''}
                    onChange={e => set(f.key, e.target.value)}
                    className={`editor-input editor-select w-full ${isFieldInvalid(f) ? 'border-red!' : ''}`}
                    autoFocus={f === fields[0]}
                  >
                    {f.placeholder && (
                      <option value="" disabled>{f.placeholder}</option>
                    )}
                    {f.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    inputMode={f.inputMode}
                    placeholder={f.placeholder}
                    value={values[f.key] ?? ''}
                    onChange={e => set(f.key, e.target.value)}
                    autoComplete="off"
                    className={`editor-input ${isFieldInvalid(f) ? 'border-red!' : ''}`}
                    autoFocus={f === fields[0]}
                  />
                )}
                {isFieldInvalid(f) && (
                  <p className="text-xs text-red mt-1">
                    {t('crud.requiredField', { field: f.label })}
                  </p>
                )}
              </>
            )}
            {f.info && !isFieldInvalid(f) && (
              <p className="text-xs text-text-muted mt-0.5">{f.info}</p>
            )}
          </div>
        ))}
        {renderExtraFormContent?.(values, set)}
      </div>
    </Dialog>
  );
}

/* -- CRUD Table --------------------------------------------------------- */

interface CrudTableProps<T extends { id: string }> {
  items: T[];
  columns: CrudColumn<T>[];
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  readOnly?: boolean;
}

function CrudTable<T extends { id: string }>({ items, columns, onEdit, onDelete, readOnly }: CrudTableProps<T>) {
  const { t } = useI18n();
  return (
    <div className="card overflow-hidden">
      {/* Desktop table */}
      <table className="w-full border-collapse hidden md:table">
        <thead>
          <tr className="divider">
            {columns.map(col => (
              <th
                key={String(col.key)}
                className={`px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted ${col.className ?? ''}`}
              >
                {col.label}
              </th>
            ))}
            {!readOnly && (
              <th className="w-[80px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id} className={`hover:bg-bg-hover transition-colors ${i < items.length - 1 ? 'divider' : ''}`}>
              {columns.map(col => (
                <td
                  key={String(col.key)}
                  className={`px-4 py-3 text-sm text-text-primary ${col.className ?? ''}`}
                >
                  {col.render
                    ? col.render(item)
                    : String((item as Record<string, unknown>)[String(col.key)] ?? '')}
                </td>
              ))}
              {!readOnly && (
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(item)}
                      className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                      title={t('common.edit')}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(item)}
                      className="p-1.5 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                      title={t('common.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile list */}
      <div className="md:hidden">
        {items.map((item, i) => (
          <div key={item.id} className={`p-4 hover:bg-bg-hover transition-colors ${i < items.length - 1 ? 'divider' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 space-y-1">
                {columns.map(col => (
                  <div key={String(col.key)} className="text-sm">
                    <span className="text-text-muted text-xs">{col.label}: </span>
                    <span className="text-text-primary">
                      {col.render
                        ? col.render(item)
                        : String((item as Record<string, unknown>)[String(col.key)] ?? '—')}
                    </span>
                  </div>
                ))}
              </div>
              {!readOnly && (
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => onEdit(item)}
                    className="p-2 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="p-2 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
